/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Join Email Invite Page - Landing page for email invitation links
 * Validates the invite token and redirects to registration with email pre-filled
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2, AlertCircle, CheckCircle, Gift, ArrowRight, Clock, Mail } from 'lucide-react'
import { validateInviteToken } from '../services/referralService'
import { createLogger } from '../utils/logger'

const logger = createLogger('JoinEmailInvite')

const JoinEmailInvite = () => {
  const { token } = useParams()
  const navigate = useNavigate()

  const [isValidating, setIsValidating] = useState(true)
  const [validation, setValidation] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (token) {
      validateToken()
    } else {
      setError('No invitation token provided')
      setIsValidating(false)
    }
  }, [token])

  const validateToken = async () => {
    setIsValidating(true)
    setError(null)

    try {
      const result = await validateInviteToken(token)

      if (result.success && result.data?.valid) {
        setValidation(result.data)
        // Auto-redirect after a brief delay to show success
        setTimeout(() => {
          navigate(
            `/register?ref=${encodeURIComponent(result.data.referralCode)}&email=${encodeURIComponent(result.data.email)}`,
            {
              state: {
                referrerName: result.data.referrerName,
                skipWaitlist: true,
                prefillEmail: result.data.email
              }
            }
          )
        }, 2500)
      } else {
        setError(result.data?.error || 'Invalid or expired invitation')
        setValidation(null)
      }
    } catch (err) {
      logger.error('Error validating invite:', err)
      setError('Failed to validate invitation. Please try again.')
      setValidation(null)
    } finally {
      setIsValidating(false)
    }
  }

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <Loader2 className="w-12 h-12 mx-auto text-primary-500 animate-spin mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Validating your invitation...
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Just a moment while we verify your invite link
          </p>
        </motion.div>
      </div>
    )
  }

  // Error state
  if (error) {
    const isExpired = error.toLowerCase().includes('expired')

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div
            className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 ${
              isExpired ? 'bg-yellow-100 dark:bg-yellow-900/20' : 'bg-red-100 dark:bg-red-900/20'
            }`}
          >
            {isExpired ? (
              <Clock className="w-8 h-8 text-yellow-500" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-500" />
            )}
          </div>

          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {isExpired ? 'Invitation Expired' : 'Invalid Invitation'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {isExpired
              ? 'This invitation has expired. Ask your friend to send you a new one!'
              : error}
          </p>

          <div className="space-y-3">
            <Link
              to="/waitlist"
              className="block w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
            >
              Join the Waitlist Instead
            </Link>
            <Link
              to="/login"
              className="block w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </motion.div>
      </div>
    )
  }

  // Success state - brief display before redirect
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-20 h-20 mx-auto bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-6"
        >
          <Gift className="w-10 h-10 text-green-500" />
        </motion.div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">You're Invited!</h2>

        {validation?.referrerName && (
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            <span className="font-medium text-primary-600 dark:text-primary-400">
              {validation.referrerName}
            </span>{' '}
            invited you to join Deaveris
          </p>
        )}

        {validation?.email && (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 mb-4">
            <div className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400">
              <Mail className="w-4 h-4" />
              <span className="text-sm">{validation.email}</span>
            </div>
          </div>
        )}

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">Skip the waitlist - Direct access!</span>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Redirecting to registration...</span>
        </div>

        <button
          onClick={() =>
            navigate(
              `/register?ref=${encodeURIComponent(validation.referralCode)}&email=${encodeURIComponent(validation.email)}`
            )
          }
          className="mt-4 inline-flex items-center gap-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
        >
          Continue to registration
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>
    </div>
  )
}

export default JoinEmailInvite
